import { useCallback, useEffect, useRef } from "react"
import {
  ensurePusher,
  subscribeChannel,
  unsubscribeChannel,
} from "../services/pusher"
import {
  CALL_RESPONSE_CREATED_EVENTS,
  CALL_RESPONSE_DELETED_EVENTS,
  CALL_RESPONSE_LIKE_EVENTS,
  CALL_RESPONSE_LIKE_REMOVED_EVENTS,
  CALL_RESPONSE_UPDATED_EVENTS,
  getCallDetailChannelName,
  matchesCallRealtime,
  parseCallResponseDeletedPayload,
  parseCallResponseLikePayload,
  parseCallResponsePayload,
} from "../constants/callRealtime"

const bindNamedEvents = (target, names, handler) => {
  if (!target || typeof target.bind !== "function") return
  names.forEach(name => target.bind(name, handler))
}

const unbindNamedEvents = (target, names, handler) => {
  if (!target || typeof target.unbind !== "function") return
  names.forEach(name => target.unbind(name, handler))
}

/**
 * Live call-response thread on public channel call-detail.{callId}.
 * Updates local list state only — does not refetch REST.
 */
const useCallResponseRealtime = ({
  callId,
  enabled = true,
  onCreated,
  onUpdated,
  onDeleted,
  onLike,
} = {}) => {
  const callbacksRef = useRef({ onCreated, onUpdated, onDeleted, onLike })
  callbacksRef.current = { onCreated, onUpdated, onDeleted, onLike }

  const handleCreated = useCallback(
    payload => {
      const parsed = parseCallResponsePayload(payload)
      if (!parsed?.response) return
      if (!matchesCallRealtime(callId, parsed.callId)) return
      callbacksRef.current.onCreated?.(parsed.response)
    },
    [callId],
  )

  const handleUpdated = useCallback(
    payload => {
      const parsed = parseCallResponsePayload(payload)
      if (!parsed?.response) return
      if (!matchesCallRealtime(callId, parsed.callId)) return
      callbacksRef.current.onUpdated?.(parsed.response)
    },
    [callId],
  )

  const handleDeleted = useCallback(
    payload => {
      const parsed = parseCallResponseDeletedPayload(payload)
      if (!parsed?.responseId) return
      if (!matchesCallRealtime(callId, parsed.callId)) return
      callbacksRef.current.onDeleted?.(parsed.responseId)
    },
    [callId],
  )

  const handleLike = useCallback(
    payload => {
      const parsed = parseCallResponseLikePayload(payload)
      if (!parsed?.commentId) return
      if (!matchesCallRealtime(callId, parsed.callId)) return
      callbacksRef.current.onLike?.({ ...parsed, removed: false })
    },
    [callId],
  )

  const handleLikeRemoved = useCallback(
    payload => {
      const parsed = parseCallResponseLikePayload(payload)
      if (!parsed?.commentId) return
      if (!matchesCallRealtime(callId, parsed.callId)) return
      callbacksRef.current.onLike?.({ ...parsed, removed: true })
    },
    [callId],
  )

  useEffect(() => {
    if (!enabled || !callId) return undefined

    const channelName = getCallDetailChannelName(callId)
    if (!channelName) return undefined

    const pusher = ensurePusher()
    if (!pusher) return undefined

    const channel = subscribeChannel(channelName)
    if (!channel) return undefined

    const unbindAll = () => {
      unbindNamedEvents(channel, CALL_RESPONSE_CREATED_EVENTS, handleCreated)
      unbindNamedEvents(channel, CALL_RESPONSE_UPDATED_EVENTS, handleUpdated)
      unbindNamedEvents(channel, CALL_RESPONSE_DELETED_EVENTS, handleDeleted)
      unbindNamedEvents(channel, CALL_RESPONSE_LIKE_EVENTS, handleLike)
      unbindNamedEvents(
        channel,
        CALL_RESPONSE_LIKE_REMOVED_EVENTS,
        handleLikeRemoved,
      )
    }

    const bindAll = () => {
      unbindAll()
      bindNamedEvents(channel, CALL_RESPONSE_CREATED_EVENTS, handleCreated)
      bindNamedEvents(channel, CALL_RESPONSE_UPDATED_EVENTS, handleUpdated)
      bindNamedEvents(channel, CALL_RESPONSE_DELETED_EVENTS, handleDeleted)
      bindNamedEvents(channel, CALL_RESPONSE_LIKE_EVENTS, handleLike)
      bindNamedEvents(
        channel,
        CALL_RESPONSE_LIKE_REMOVED_EVENTS,
        handleLikeRemoved,
      )
    }

    bindAll()
    pusher.connection.bind("connected", bindAll)

    return () => {
      pusher.connection.unbind("connected", bindAll)
      unbindAll()
      unsubscribeChannel(channelName)
    }
  }, [
    callId,
    enabled,
    handleCreated,
    handleDeleted,
    handleLike,
    handleLikeRemoved,
    handleUpdated,
  ])
}

export default useCallResponseRealtime
